import { useState, useEffect } from 'react';
import { useSchool } from '@/contexts/SchoolContext';
import { useAuth } from '@/features/auth/hooks';
import { timetablesService } from '@/services/timetablesService';
import { timetableAssignmentsService } from '@/services/timetableAssignmentsService';
import { dayTemplatesService } from '@/services/dayTemplatesService';
import {
  formatTimeRange,
  getPeriodDisplayLabel,
  PERIOD_TYPES,
} from '@/utils/timetableHelpers';
import { mapAssignmentsToPeriods } from '@/utils/timetableAssignmentMapping';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

const PERIOD_TYPE_STYLES = {
  [PERIOD_TYPES.BREAK]: { bg: 'bg-secondary/15', text: 'text-secondary' },
  [PERIOD_TYPES.ASSEMBLY]: { bg: 'bg-accent/15', text: 'text-accent' },
  [PERIOD_TYPES.ADMIN]: { bg: 'bg-info/15', text: 'text-info' },
  [PERIOD_TYPES.READING]: { bg: 'bg-success/15', text: 'text-success' },
  [PERIOD_TYPES.TUTORIAL]: { bg: 'bg-warning/15', text: 'text-warning' },
  [PERIOD_TYPES.NON_CLASS]: { bg: 'bg-neutral/15', text: 'text-neutral' },
  [PERIOD_TYPES.OTHER]: { bg: 'bg-base-300/50', text: 'text-base-content/50' },
};

const REFRESH_INTERVAL = 30_000;

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const CACHE_PREFIX = 'swan_cached_timetable_panel_';
const cacheKey = (schoolId, uid) => `${CACHE_PREFIX}${schoolId}_${uid}`;
const readCache = (key) => {
  try { return JSON.parse(sessionStorage.getItem(key)) ?? null; }
  catch { return null; }
};
const writeCache = (key, value) => {
  try { sessionStorage.setItem(key, JSON.stringify(value)); }
  catch { /* quota */ }
};

export default function TimetablePanel() {
  const { schoolId } = useSchool();
  const { user } = useAuth();
  const initialKey = schoolId && user?.uid ? cacheKey(schoolId, user.uid) : null;
  const [data, setData] = useState(() => (initialKey ? readCache(initialKey) : null));
  const [loading, setLoading] = useState(() => !initialKey || readCache(initialKey) == null);

  useEffect(() => {
    if (!schoolId) {
      setData({ state: 'no-school' });
      setLoading(false);
      return;
    }
    if (!user?.uid) {
      setLoading(false);
      return;
    }

    const key = cacheKey(schoolId, user.uid);
    const cached = readCache(key);
    if (cached != null) {
      setData(cached);
      setLoading(false);
    }

    let cancelled = false;
    const commit = (next) => {
      if (cancelled) return;
      setData(next);
      setLoading(false);
      writeCache(key, next);
    };
    const load = async () => {
      try {
        const now = new Date();
        const timetable = await timetablesService.getActive(schoolId, now);
        if (!timetable) {
          commit({ state: 'no-timetable' });
          return;
        }

        const [teacherAssignments, dayTemplate] = await Promise.all([
          timetableAssignmentsService.getForTeacher(schoolId, timetable.id, user.uid),
          timetablesService.getCurrentDayTemplate(schoolId, now),
        ]);

        const cycleDay = timetable
          ? timetablesService.getCycleDay(now, timetable.startDate, timetable.cycleLength, timetable.daysOfWeek)
          : null;
        const scheduledTemplateId = cycleDay != null
          ? timetable.cyclePattern?.[cycleDay]
          : null;
        const scheduledTemplate = scheduledTemplateId === dayTemplate?.id
          ? dayTemplate
          : scheduledTemplateId
            ? await dayTemplatesService.getById(schoolId, scheduledTemplateId)
            : null;

        const cycleDayName = cycleDay != null ? `Day ${cycleDay + 1}` : null;
        const dayLabel = DAY_NAMES[now.getDay()];

        if (!dayTemplate) {
          const next = await getNextSchoolDayPreview(timetable, teacherAssignments, schoolId, user.uid);
          commit({ state: 'no-school-today', dayLabel, next });
          return;
        }

        const allPeriods = dayTemplate.periods || [];
        const currentPeriod = dayTemplatesService.getCurrentPeriod(dayTemplate, now);
        const todaysAssignments = cycleDay != null
          ? teacherAssignments.filter((a) => a.cycleDayIndex === cycleDay)
          : [];

        const items = mapAssignmentsToPeriods({
          assignments: todaysAssignments,
          displayedPeriods: allPeriods,
          scheduledTemplate,
        }).map((item) => ({
          ...item,
          isCurrent: currentPeriod?.order === item.period.order,
        }));

        commit({ state: 'today', dayLabel, cycleDayName, items });
      } catch (err) {
        console.error('[TimetablePanel] Load error:', err);
        if (!cancelled && !readCache(key)) {
          setData({ state: 'error' });
          setLoading(false);
        }
      }
    };

    const refresh = () => {
      try { sessionStorage.removeItem(key); }
      catch { /* ignore */ }
      load();
    };

    load();
    const interval = setInterval(load, REFRESH_INTERVAL);
    const unsubscribeActive = timetablesService.subscribeActive(schoolId, refresh);
    window.addEventListener('swan:timetable-updated', refresh);
    return () => {
      cancelled = true;
      clearInterval(interval);
      unsubscribeActive();
      window.removeEventListener('swan:timetable-updated', refresh);
    };
  }, [schoolId, user?.uid]);

  if (loading) {
    return (
      <div className="p-4 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!data) return null;

  if (data.state === 'no-school') {
    return <EmptyState title="No school selected" hint="Pick a school to see its timetable." />;
  }

  if (data.state === 'no-timetable') {
    return <EmptyState title="No active timetable" hint="Set one up from the Timetable builder." />;
  }

  if (data.state === 'error') {
    return <EmptyState title="Couldn't load timetable" hint="Try reopening the panel." />;
  }

  if (data.state === 'no-school-today') {
    return (
      <div className="p-4 space-y-3">
        <Header dayLabel={data.dayLabel} cycleDayName={null} subtitle="No classes scheduled" />
        {data.next ? (
          <div className="space-y-2">
            <div className="text-xs text-base-content/50 px-1">
              Next school day — {data.next.isNextDay ? 'Tomorrow' : data.next.dayLabel} · {data.next.cycleDayName}
            </div>
            {data.next.periods.map((item, i) => (
              <PeriodBlock key={i} period={item.period} assignment={item.assignment} />
            ))}
          </div>
        ) : (
          <div className="text-sm text-base-content/50 px-1">No upcoming school days this week.</div>
        )}
      </div>
    );
  }

  // state === 'today'
  return (
    <div className="p-4 space-y-3">
      <Header dayLabel={data.dayLabel} cycleDayName={data.cycleDayName} />
      <div className="space-y-2">
        {data.items.length === 0 ? (
          <div className="text-sm text-base-content/50 px-1">No periods scheduled today.</div>
        ) : (
          data.items.map((item, i) => (
            <PeriodBlock
              key={i}
              period={item.period}
              assignment={item.assignment}
              isCurrent={item.isCurrent}
            />
          ))
        )}
      </div>
    </div>
  );
}

function Header({ dayLabel, cycleDayName, subtitle }) {
  return (
    <div className="flex items-center justify-between px-1">
      <div>
        <div className="text-sm font-semibold">{dayLabel}</div>
        {subtitle && <div className="text-xs text-base-content/50">{subtitle}</div>}
      </div>
      {cycleDayName && (
        <span className="badge badge-ghost badge-sm">{cycleDayName}</span>
      )}
    </div>
  );
}

function EmptyState({ title, hint }) {
  return (
    <div className="p-6 text-center space-y-1">
      <div className="text-sm font-medium text-base-content/70">{title}</div>
      <div className="text-xs text-base-content/50">{hint}</div>
    </div>
  );
}

function PeriodBlock({ period, assignment, isCurrent }) {
  const isClassPeriod = period.type === PERIOD_TYPES.CLASS || period.type === PERIOD_TYPES.TUTORIAL;
  const typeStyle = PERIOD_TYPE_STYLES[period.type] || PERIOD_TYPE_STYLES[PERIOD_TYPES.OTHER];
  const periodLabel = getPeriodDisplayLabel(period);
  const timeRange = formatTimeRange(period.startTime, period.endTime);
  const ring = isCurrent ? 'ring-2 ring-primary' : '';

  if (!isClassPeriod) {
    return (
      <div className={`rounded-lg px-3 py-2 ${typeStyle.bg} ${ring}`}>
        <div className="flex items-center justify-between">
          <div className={`text-sm font-semibold ${typeStyle.text}`}>{periodLabel}</div>
          <div className="text-xs text-base-content/50">{timeRange}</div>
        </div>
      </div>
    );
  }

  if (assignment) {
    const isActivity = !assignment.subjectId && assignment.activityTypeId;
    const label = isActivity
      ? assignment.activityTypeCode || assignment.activityTypeName
      : assignment.subjectCode || assignment.subjectName;
    const color = (isActivity ? assignment.activityTypeColor : assignment.subjectColor) || 'var(--color-neutral)';
    return (
      <div
        className={`rounded-lg px-3 py-2 text-white ${ring}`}
        style={{ backgroundColor: color }}
      >
        <div className="flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-sm font-bold truncate">
              {isActivity ? label : [label, assignment.className].filter(Boolean).join(' — ')}
            </div>
            <div className="text-xs text-white/70">{periodLabel}</div>
          </div>
          <div className="text-xs text-white/70 shrink-0 ml-2">{timeRange}</div>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-lg px-3 py-2 bg-success/10 ${ring}`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-success">Free</div>
          <div className="text-xs text-base-content/50">{periodLabel}</div>
        </div>
        <div className="text-xs text-base-content/50">{timeRange}</div>
      </div>
    </div>
  );
}

async function getNextSchoolDayPreview(timetable, teacherAssignments, schoolId, teacherId) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  for (let daysAhead = 1; daysAhead <= 7; daysAhead++) {
    const futureDate = new Date(today);
    futureDate.setDate(futureDate.getDate() + daysAhead);

    const futureTimetable = await timetablesService.getActive(schoolId, futureDate);
    if (!futureTimetable) continue;

    const cycleDay = timetablesService.getCycleDay(
      futureDate, futureTimetable.startDate, futureTimetable.cycleLength, futureTimetable.daysOfWeek
    );
    if (cycleDay == null) continue;

    const dayTemplate = await timetablesService.getCurrentDayTemplate(schoolId, futureDate);
    if (!dayTemplate?.periods?.length) continue;
    const scheduledTemplateId = futureTimetable.cyclePattern?.[cycleDay];
    const scheduledTemplate = scheduledTemplateId === dayTemplate.id
      ? dayTemplate
      : scheduledTemplateId
        ? await dayTemplatesService.getById(schoolId, scheduledTemplateId)
        : null;

    const assignments = futureTimetable.id === timetable.id
      ? teacherAssignments
      : await timetableAssignmentsService.getForTeacher(schoolId, futureTimetable.id, teacherId);
    const dayAssignments = assignments.filter((a) => a.cycleDayIndex === cycleDay);
    const classPeriods = dayTemplate.periods.filter(
      (p) => p.type === PERIOD_TYPES.CLASS || p.type === PERIOD_TYPES.TUTORIAL
    );
    const preview = mapAssignmentsToPeriods({
      assignments: dayAssignments,
      displayedPeriods: classPeriods,
      scheduledTemplate,
    }).slice(0, 3);

    if (preview.length === 0) continue;

    return {
      date: futureDate,
      isNextDay: daysAhead === 1,
      dayLabel: DAY_NAMES[futureDate.getDay()],
      cycleDayName: `Day ${cycleDay + 1}`,
      periods: preview,
    };
  }

  return null;
}
